import { createSelector } from "@reduxjs/toolkit";
import {
  getTotalPriceText,
  twoDecimalFormatter,
  getRewardsString,
  CurrencyFormatters,
  IconName,
  getCheckoutCreditBreakdown,
  IHotelPriceLineItem,
  emailRegex,
  phoneRegex,
  roundToTwoDecimals,
  getAgentErrorSubtitle,
  getAgentErrorTitle,
  paymentTextConstants,
  getCreditsAvailableByRestrictions,
  getCreditBreakdownAvailableByRestrictions,
  getCreditBreakdownIneligibleByRestrictions,
} from "halifax";
import {
  CallState,
  CreateHomeProductRequest,
  RewardsPrice,
  FiatPrice,
  PaymentSplitRequestEnum,
  PaymentV2Enum,
  Prices,
  PaymentErrorEnum,
  PaymentVerifyResultEnum,
  ErrorTitles,
  GroupedHomesLineItemEnum,
  ITrackingProperties,
  CompleteBuyVacationRentalProperties,
  AvailableProduct,
  AvailableProductEnum,
  MerchantOfRecord,
  PaymentSchedule,
  ProductClassEnum,
  OffersForProductsRequest,
  RestrictedToProductsEnum,
  CreditCollectionTypeEnum,
} from "redmond";
import {
  ErrorCode,
  Payment,
  PaymentError,
  PaymentOpaqueValue,
  ProductError,
  PurchaseError,
  PurchaseErrorEnum,
} from "@b2bportal/purchase-api";
import { isCorpTenant } from "@capone/common";
import dayjs from "dayjs";
import * as textConstants from "./textConstants";
import { IStoreState } from "../../../../reducers/types";
import {
  getPaymentMethodRewardsAccountId,
  getPaymentMethods,
  getRewardsPaymentAccount,
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentInRewardsCurrency,
  getSelectedPaymentMethodId,
  getTravelWalletItemsToApply,
  getUserSelectedTravelerId,
  getIsSelfBooked,
  getConfirmationEmail,
  getConfirmationPhoneNumber,
  getRewardsPaymentAccountReferenceId,
  Progress,
  CheckoutSteps,
  verifyPaymentMethodResultSelector,
  getUserPassengerCallState,
  getVerifyPaymentMethodCallState,
  getRewardsConversionFailed,
  getCredit,
  getSelectedPaymentCardType,
  getOffers,
  getTravelWalletOfferToApplyAmount,
} from "./premierCollection";
import {
  getRewardsAccountWithLargestValue,
  getRewardsAccounts,
} from "../../../rewards/reducer";
import {
  getNotifyIfShopAndBookPriceDiffer,
  getTabView,
  getVacationRentalShopSelectedListing,
  getViewedVacationRentalDetailsProperties,
} from "../../../vacation-rental-shop/reducer";
import {
  getAdultsCount,
  getChildrenCount,
  getFromDate,
  getPetsCount,
  getUntilDate,
} from "../../../search/reducer";
import { config } from "../../../../api/config";
import { getCreditBreakdown } from "../../../travel-wallet/reducer";
import { getSearchedNightCount } from "../../../availability/reducer";

export const getVacationRentalsSchedulePriceQuoteCallState = (
  state: IStoreState
) => state.premierCollectionBook.scheduleVacationRentalsPriceQuoteCallState;

export const getVacationRentalsPollPriceQuoteCallState = (state: IStoreState) =>
  state.premierCollectionBook.pollVacationRentalsPriceQuoteCallState;

export const getVacationRentalsPriceQuote = (state: IStoreState) =>
  state.premierCollectionBook.vacationRentalsPriceQuote;

export const getScheduleVRBookCallState = (state: IStoreState) =>
  state.premierCollectionBook.scheduleVacationRentalsBookCallState;

export const getVRConfirmationDetailsCallState = (state: IStoreState) =>
  state.premierCollectionBook.pollVacationRentalsConfirmationDetailsCallState;

export const getVRConfirmationDetailsError = (state: IStoreState) =>
  state.premierCollectionBook.vacationRentalsConfirmationDetailsErrors;

export const getVacationRentalsPriceQuotePricing = createSelector(
  getVacationRentalsPriceQuote,
  (priceQuote) => priceQuote?.rate
);

export const getPollVacationRentalsPriceQuoteCallState = (state: IStoreState) =>
  state.premierCollectionBook.pollVacationRentalsPriceQuoteCallState;

export const getScheduleVacationRentalsPriceQuoteCallState = (
  state: IStoreState
) => state.premierCollectionBook.scheduleVacationRentalsPriceQuoteCallState;

export const getVacationRentalConfirmationDetails = (state: IStoreState) =>
  state.premierCollectionBook.vacationRentalsConfirmationDetails;

export const getAddMainGuestCallState = (state: IStoreState) =>
  state.premierCollectionBook.addMainGuestCallState;

export const getVacationRentalsPricingTotal = createSelector(
  getVacationRentalsPriceQuotePricing,
  (pricing) => (pricing ? pricing.detailedPrice.payNowTotal : null)
);

export const getTotalVacationRentalsToPay = createSelector(
  getTravelWalletItemsToApply,
  getVacationRentalsPricingTotal,
  (travelWalletItemsToApply, priceQuotePricingTotal): Prices | null => {
    const total = priceQuotePricingTotal ?? null;
    const rewardsTotal = {};
    if (
      total &&
      (travelWalletItemsToApply.offerToApply ||
        travelWalletItemsToApply.creditToApply)
    ) {
      let walletItemsTotal: number =
        travelWalletItemsToApply.offerToApply?.amount.amount || 0;

      if (travelWalletItemsToApply.creditToApply) {
        const maxApplicableCredit =
          travelWalletItemsToApply.offerToApply?.maxApplicableCredit?.amount;
        if (
          maxApplicableCredit &&
          maxApplicableCredit >
            travelWalletItemsToApply.creditToApply.amount.amount
        ) {
          walletItemsTotal += maxApplicableCredit;
        } else {
          walletItemsTotal +=
            travelWalletItemsToApply.creditToApply.amount.amount;
        }
      }
      Object.keys(total.rewards).forEach((accountId) => {
        if (total) {
          let rewardTotalValue = total.rewards[accountId].value;
          rewardTotalValue =
            total.rewards[accountId].currency === "Cash"
              ? rewardTotalValue + walletItemsTotal
              : rewardTotalValue + walletItemsTotal * 100;
          rewardsTotal[accountId] = {
            currency: total.rewards[accountId].currency,
            currencyDescription: total.rewards[accountId].currencyDescription,
            value: rewardTotalValue > 0 ? rewardTotalValue : 0,
          };
        }
      });
      return {
        fiat: {
          currencyCode: total.fiat.currencyCode,
          currencySymbol: total.fiat.currencySymbol,
          value:
            total.fiat.value + walletItemsTotal > 0
              ? total.fiat.value + walletItemsTotal
              : 0,
        },
        rewards: rewardsTotal,
      };
    }
    return priceQuotePricingTotal ?? null;
  }
);

export const getTotalVacationRentalsCreditCardPaymentRequired = createSelector(
  getTotalVacationRentalsToPay,
  getRewardsPaymentInFiatCurrency,
  (estimate, rewardsPaymentAmount) => {
    if (!estimate) return "";

    if (!rewardsPaymentAmount) {
      return getTotalPriceText({
        price: estimate.fiat,
        priceFormatter: twoDecimalFormatter,
      });
    }

    const remainder = estimate.fiat.value - rewardsPaymentAmount.value;
    const total = remainder <= 0 ? 0 : remainder;

    return getTotalPriceText({
      price: {
        currencyCode: estimate.fiat.currencyCode,
        currencySymbol: estimate.fiat.currencySymbol,
        value: total,
      },
      priceFormatter: twoDecimalFormatter,
    });
  }
);

export const getVacationRentalsTravelWalletOfferToApplyAmount = createSelector(
  getTravelWalletItemsToApply,

  (travelWalletItemsToApply) => {
    if (travelWalletItemsToApply.offerToApply) {
      return travelWalletItemsToApply.offerToApply.amount.amount * -1;
    }
    return 0;
  }
);

export const getVacationRentalsTravelWalletCreditToApplyAmount = createSelector(
  getTravelWalletItemsToApply,
  getVacationRentalsPricingTotal,
  getVacationRentalsTravelWalletOfferToApplyAmount,
  (travelWalletItemsToApply, priceQuotePricingTotal, offerAppliedAmount) => {
    const totalFiatValue = priceQuotePricingTotal?.fiat.value || 0;
    const totalFiatValueWithOfferApplied: number =
      totalFiatValue - offerAppliedAmount;
    if (
      travelWalletItemsToApply.creditToApply &&
      totalFiatValueWithOfferApplied > 0
    ) {
      const maxApplicableCredit =
        travelWalletItemsToApply.offerToApply?.maxApplicableCredit?.amount;
      const amount =
        maxApplicableCredit &&
        maxApplicableCredit <
          travelWalletItemsToApply.creditToApply.amount.amount
          ? maxApplicableCredit
          : travelWalletItemsToApply.creditToApply.amount.amount;
      if (totalFiatValueWithOfferApplied < amount * -1) {
        if (
          maxApplicableCredit &&
          maxApplicableCredit <
            travelWalletItemsToApply.creditToApply.amount.amount &&
          travelWalletItemsToApply.creditToApply.amount.amount * -1 <
            totalFiatValueWithOfferApplied
        ) {
          return travelWalletItemsToApply.creditToApply.amount.amount * -1;
        }
        return totalFiatValueWithOfferApplied;
      }
      if (
        maxApplicableCredit &&
        maxApplicableCredit <
          travelWalletItemsToApply.creditToApply.amount.amount
      ) {
        return travelWalletItemsToApply.creditToApply.amount.amount * -1;
      }
      return amount * -1;
    }
    return 0;
  }
);

export const getApplicableCreditForHomes = createSelector(
  getCredit,
  getVacationRentalShopSelectedListing,
  getSearchedNightCount,
  (credit, selectedListing, nightCount) => {
    if (!credit) return credit;
    return getCreditsAvailableByRestrictions(
      {
        product: RestrictedToProductsEnum.Homes,
        collectionType: selectedListing?.listing.listingCollection as unknown as CreditCollectionTypeEnum,
        nights: nightCount ?? undefined,
      },
      credit
    );
  }
);

export const getApplicableCreditsBreakdownForHomes = createSelector(
  getCreditBreakdown,
  getVacationRentalShopSelectedListing,
  getSearchedNightCount,
  (creditBreakdown, selectedListing, nightCount) =>
    getCreditBreakdownAvailableByRestrictions(
      {
        product: RestrictedToProductsEnum.Homes,
        collectionType: selectedListing?.listing.listingCollection as unknown as CreditCollectionTypeEnum,
        nights: nightCount ?? undefined,
      },
      creditBreakdown
    )
);

export const getIneligibleCreditsBreakdownForHomes = createSelector(
  getCreditBreakdown,
  getVacationRentalShopSelectedListing,
  getSearchedNightCount,
  (creditBreakdown, selectedListing, nightCount) =>
    getCreditBreakdownIneligibleByRestrictions(
      {
        product: RestrictedToProductsEnum.Homes,
        collectionType: selectedListing?.listing.listingCollection as unknown as CreditCollectionTypeEnum,
        nights: nightCount ?? undefined,
      },
      creditBreakdown
    )
);

export const getVacationRentalsMaxApplicableTravelWalletCreditAmount =
  createSelector(
    getApplicableCreditForHomes,
    getVacationRentalsPricingTotal,
    getVacationRentalsTravelWalletOfferToApplyAmount,
    (credit, priceQuotePricingTotal, offerAppliedAmount) => {
      if (priceQuotePricingTotal && credit) {
        if (
          priceQuotePricingTotal.fiat.value - offerAppliedAmount <
          credit.amount.amount * -1
        ) {
          return (priceQuotePricingTotal.fiat.value - offerAppliedAmount) * -1;
        }
        return credit.amount.amount;
      }
      return 0;
    }
  );

export const getVacationRentalsTripTotalInPrices = createSelector(
  getVacationRentalsPriceQuote,
  getRewardsPaymentAccount,
  getRewardsAccountWithLargestValue,
  getVacationRentalsPriceQuotePricing,
  (
    priceQuote,
    rewardsPaymentAccount,
    rewardsAccountWithLargestValue,
    priceQuotePricing
  ): { fiat: FiatPrice; rewards?: RewardsPrice } | null => {
    const activeRewardsAccount =
      rewardsPaymentAccount ?? rewardsAccountWithLargestValue;

    const includeRewards = activeRewardsAccount?.allowRewardsRedemption ?? true;

    if (priceQuote && priceQuotePricing && activeRewardsAccount) {
      return {
        fiat: priceQuotePricing.detailedPrice.payNowTotal.fiat,
        rewards: includeRewards
          ? priceQuotePricing.detailedPrice.payNowTotal.rewards[
              activeRewardsAccount.accountReferenceId
            ]
          : undefined,
      };
    }
    return null;
  }
);

export const getVacationRentalsRewardsAndTotalLineItems = createSelector(
  getRewardsPaymentAccount,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentInFiatCurrency,
  getTotalVacationRentalsCreditCardPaymentRequired,
  getSelectedPaymentMethodId,
  getPaymentMethods,
  getTravelWalletItemsToApply,
  getVacationRentalsTravelWalletOfferToApplyAmount,
  getVacationRentalsTravelWalletCreditToApplyAmount,
  (
    rewardsPaymentAccount,
    rewardsPayment,
    rewardsInFiat,
    totalAmountDueOnCredit,
    paymentMethodId,
    paymentMethods,
    travelWalletItemsToApply,
    offerAmountToApply,
    creditAmountToApply
  ) => {
    const rewardsLineItems = [];

    if (
      rewardsPaymentAccount &&
      (rewardsPaymentAccount.allowRewardsRedemption ?? true)
    ) {
      const fiatPrice = rewardsInFiat ?? {
        currencyCode: rewardsPaymentAccount?.rewardsCashEquivalent.currencyCode,
        currencySymbol:
          rewardsPaymentAccount?.rewardsCashEquivalent.currencySymbol,
        value: 0,
      };

      const priceText = getTotalPriceText({
        price: {
          ...fiatPrice,
          value: fiatPrice.value,
        },
        priceFormatter: twoDecimalFormatter,
      });

      const rewardsText = rewardsPayment
        ? `- ${getRewardsString(rewardsPayment)}`
        : "";
      rewardsLineItems.push({
        title: `${rewardsPaymentAccount?.productDisplayName} ${textConstants.REWARDS}`,
        value: `- ${priceText}`,
        rewardsValue: rewardsText,
      });
    }

    const paymentMethod = paymentMethods.find((p) => p.id === paymentMethodId);
    if (
      travelWalletItemsToApply.offerToApply ||
      travelWalletItemsToApply.creditToApply
    ) {
      const travelWalletItems = [];
      if (travelWalletItemsToApply.offerToApply && offerAmountToApply) {
        travelWalletItems.push({
          title: textConstants.TRAVEL_OFFER_APPLIED,
          value: getTotalPriceText({
            price: {
              value: offerAmountToApply * -1,
              currencyCode:
                travelWalletItemsToApply.offerToApply?.amount.currency,
              currencySymbol: CurrencyFormatters.getSymbol(
                travelWalletItemsToApply.offerToApply?.amount.currency
              ),
            },
            priceFormatter: twoDecimalFormatter,
          }),
          isTravelOffer: true,
          isTravelCredit: false,
          icon: "offer-tag",
        });
      }
      if (travelWalletItemsToApply.creditToApply && creditAmountToApply) {
        const breakdownHasStatementCredit =
          !!travelWalletItemsToApply.creditToApply.breakdown?.some(
            (detail) =>
              detail.CreditDetail === "Statement" &&
              Math.abs(detail.usableAmount.amount) > 0
          );
        travelWalletItems.push({
          title: breakdownHasStatementCredit
            ? textConstants.TOTAL_TRAVEL_CREDITS_APPLIED
            : textConstants.TRAVEL_CREDITS_APPLIED,
          value: getTotalPriceText({
            price: {
              value: creditAmountToApply * -1,
              currencyCode:
                travelWalletItemsToApply.creditToApply?.amount.currency,
              currencySymbol: CurrencyFormatters.getSymbol(
                travelWalletItemsToApply.creditToApply?.amount.currency
              ),
            },
            priceFormatter: twoDecimalFormatter,
          }),
          isTravelOffer: false,
          isTravelCredit: true,
          icon: "piggy-bank-icon",
          className: "wallet-item",
          travelCreditBreakdown: breakdownHasStatementCredit
            ? getCheckoutCreditBreakdown(
                travelWalletItemsToApply.creditToApply.breakdown || [],
                creditAmountToApply,
                travelWalletItemsToApply.creditToApply.amount.currency
              )
            : [],
        });
      }

      return [
        ...travelWalletItems,
        ...rewardsLineItems,
        {
          title: paymentMethod
            ? `Ending in ${paymentMethod.last4}:`
            : textConstants.AMOUNT_DUE,
          value: totalAmountDueOnCredit,
          icon: IconName.Payment,
        },
      ];
    }

    return [
      ...rewardsLineItems,
      {
        title: paymentMethod
          ? `Ending in ${paymentMethod.last4}:`
          : textConstants.AMOUNT_DUE,
        value: totalAmountDueOnCredit,
        icon: IconName.Payment,
      },
    ];
  }
);

export const getVRPriceDifferenceAcknowledged = (state: IStoreState) =>
  state.premierCollectionBook.priceDifferenceAcknowledged;

export const getVRPriceDifference = createSelector(
  getNotifyIfShopAndBookPriceDiffer,
  getVacationRentalShopSelectedListing,
  getVacationRentalsPriceQuote,
  getVRPriceDifferenceAcknowledged,
  (
    notify,
    selectedHome,
    priceQuote,
    acknowledged
  ): {
    hasDifference: boolean;
    isIncrease: boolean;
    seenPrice: number;
    priceQuoteTotal: number;
    amount?: string;
  } => {
    if (
      !notify ||
      !selectedHome ||
      !selectedHome.availability.rate ||
      !priceQuote ||
      acknowledged
    ) {
      return {
        hasDifference: false,
        isIncrease: false,
        seenPrice: 0,
        priceQuoteTotal: 0,
      };
    }

    const seenPrice = selectedHome.availability.rate.price.total.fiat.value;
    const priceQuoteTotal =
      priceQuote.rate.detailedPrice.payNowTotal.fiat.value;

    return {
      hasDifference: Math.abs(priceQuoteTotal - seenPrice) >= 1,
      isIncrease: priceQuoteTotal > seenPrice,
      amount: Math.abs(priceQuoteTotal - seenPrice)
        .toFixed(0)
        .toString(),
      seenPrice,
      priceQuoteTotal,
    };
  }
);

export const getGroupedPricingLineItems = createSelector(
  getVacationRentalsPriceQuotePricing,
  (priceQuotePricing): IHotelPriceLineItem[] => {
    if (priceQuotePricing) {
      return priceQuotePricing.detailedPrice.groupedItems.map((item) => {
        const lineItems: IHotelPriceLineItem = {
          title: item.displayName,
          value: getTotalPriceText({
            price: item.total.fiat,
            priceFormatter: twoDecimalFormatter,
          }),
        };
        switch (item.GroupedHomesLineItem) {
          case GroupedHomesLineItemEnum.CleaningFees:
            return {
              ...lineItems,
              tooltip:
                "One-time fee charged to cover the cost of cleaning the space.",
            };
          case GroupedHomesLineItemEnum.Taxes:
            // If the tooltip consists of one item, where the name is the same
            // as the title of the group, the tooltip will end up being
            // redundant. In that case, we should avoid showing the tooltip.
            const toolTip =
              item.fees.length === 1 && item.fees[0].name === lineItems.title
                ? undefined
                : item.fees.map((fee) => fee.name).join(", ");
            return { ...lineItems, tooltip: toolTip };
          case GroupedHomesLineItemEnum.HostAndManagementFees:
            return {
              ...lineItems,
              tooltip:
                "These are fees that are imposed by the hosts and include other standard booking fees, which our hosts use to offer services like in-destination support.",
            };
          default:
            return lineItems;
        }
      });
    }
    return [];
  }
);

export const getVacationRentalsPricingLineItems = createSelector(
  getVacationRentalShopSelectedListing,
  getVacationRentalsPriceQuote,
  getRewardsPaymentAccount,
  getPaymentMethodRewardsAccountId,
  getRewardsAccounts,
  getVacationRentalsPriceQuotePricing,
  getGroupedPricingLineItems,
  (
    product,
    priceQuote,
    rewardsPaymentAccount,
    paymentMethodRewardsAccountId,
    rewardsAccounts,
    priceQuotePricing,
    taxesAndFeesLineItems
  ): IHotelPriceLineItem[][] => {
    if (priceQuote && priceQuotePricing && product) {
      const rewardsAccountId = rewardsPaymentAccount
        ? rewardsPaymentAccount.accountReferenceId
        : paymentMethodRewardsAccountId ||
          Object.keys(priceQuotePricing.detailedPrice.payNowTotal.rewards)[0];

      const total = priceQuotePricing.detailedPrice.payNowTotal;

      const includeRewards =
        rewardsAccounts.find(
          (account) => account.accountReferenceId === rewardsAccountId
        )?.allowRewardsRedemption ?? true;

      return [
        taxesAndFeesLineItems,
        [
          {
            title: textConstants.TOTAL,
            value: getTotalPriceText({
              price: {
                currencyCode: total.fiat.currencyCode,
                currencySymbol: total.fiat.currencySymbol,
                value: total.fiat.value,
              },
              priceFormatter: twoDecimalFormatter,
            }),
            rewardsValue: includeRewards
              ? getRewardsString(total.rewards[rewardsAccountId])
              : undefined,
            boldLabel: true,
            type: "total",
          } as IHotelPriceLineItem,
        ],
      ];
    }
    return [];
  }
);

export const getVacationRentalsIsTravelWalletCreditPaymentOnly = createSelector(
  getTotalVacationRentalsToPay,
  getTravelWalletItemsToApply,
  (total, travelWalletItemsToApply) => {
    if (
      !travelWalletItemsToApply.offerToApply &&
      travelWalletItemsToApply.creditToApply &&
      total?.fiat.value === 0
    ) {
      return true;
    }
    return false;
  }
);

export const getVacationRentalsIsTravelWalletOfferPaymentOnly = createSelector(
  getTotalVacationRentalsToPay,
  getTravelWalletItemsToApply,
  (total, travelWalletItemsToApply) => {
    if (
      travelWalletItemsToApply.offerToApply &&
      !travelWalletItemsToApply.creditToApply &&
      total?.fiat.value === 0
    ) {
      return true;
    }
    return false;
  }
);

export const getVacationRentalsIsStackedTravelWalletPaymentOnly =
  createSelector(
    getTotalVacationRentalsToPay,
    getTravelWalletItemsToApply,
    (total, travelWalletItemsToApply) => {
      if (
        travelWalletItemsToApply.offerToApply &&
        travelWalletItemsToApply.creditToApply &&
        total?.fiat.value === 0
      ) {
        return true;
      }
      return false;
    }
  );

export const getVacationRentalsIsTravelWalletPaymentOnly = createSelector(
  getVacationRentalsIsTravelWalletCreditPaymentOnly,
  getVacationRentalsIsTravelWalletOfferPaymentOnly,
  getVacationRentalsIsStackedTravelWalletPaymentOnly,
  (
    isTravelWalletCreditPaymentOnly,
    isTravelWalletOfferPaymentOnly,
    isStackedTravelWalletPaymentOnly
  ) => {
    if (
      isTravelWalletCreditPaymentOnly ||
      isTravelWalletOfferPaymentOnly ||
      isStackedTravelWalletPaymentOnly
    ) {
      return true;
    }
    return false;
  }
);

export const getVacationRentalsIsCreditCardPaymentRequired = createSelector(
  getTotalVacationRentalsToPay,
  getRewardsPaymentInFiatCurrency,
  getVacationRentalsIsTravelWalletPaymentOnly,
  (total, rewardsPaymentAmount, isTravelWalletPaymentOnly) => {
    if (isTravelWalletPaymentOnly) return false;

    if (!total) return true;
    if (!rewardsPaymentAmount) return true;

    return total.fiat.value > rewardsPaymentAmount.value;
  }
);

export const getTotalVacationRentalsCreditCardPaymentRequiredNumber =
  createSelector(
    getTotalVacationRentalsToPay,
    getRewardsPaymentInFiatCurrency,
    (estimate, rewardsPaymentAmount) => {
      if (!estimate) return null;

      if (!rewardsPaymentAmount) {
        return estimate.fiat.value;
      }

      const remainder = estimate.fiat.value - rewardsPaymentAmount.value;
      const total =
        remainder <= 0
          ? 0
          : Math.round((remainder + Number.EPSILON) * 100) / 100;
      return total;
    }
  );

export const getTotalVacationRentalsCreditCardPaymentRequiredInFiatPrice =
  createSelector(
    getTotalVacationRentalsToPay,
    getRewardsPaymentInFiatCurrency,
    (totalToPay, rewardsPaymentAmount): FiatPrice | undefined => {
      if (!totalToPay) return undefined;

      if (!rewardsPaymentAmount) return totalToPay.fiat;

      const remainder = totalToPay.fiat.value - rewardsPaymentAmount.value;
      const total = remainder <= 0 ? 0 : remainder;

      return {
        currencyCode: totalToPay.fiat.currencyCode,
        currencySymbol: totalToPay.fiat.currencySymbol,
        value: total,
      };
    }
  );

// TODO: VR - Include fulfill states
export const getVacationRentalsBookingInProgress = createSelector(
  getVacationRentalsSchedulePriceQuoteCallState,
  getVacationRentalsPollPriceQuoteCallState,
  (scheduleQuote, pollPriceQuote) =>
    scheduleQuote === CallState.InProcess ||
    pollPriceQuote === CallState.InProcess
);

export const getVacationRentalsIsBookingValid = createSelector(
  getVacationRentalsIsCreditCardPaymentRequired,
  getVRPriceDifference,
  getVRPriceDifferenceAcknowledged,
  getSelectedPaymentMethodId,
  getUserSelectedTravelerId,
  getVacationRentalsBookingInProgress,
  getConfirmationEmail,
  getConfirmationPhoneNumber,
  getVacationRentalsIsTravelWalletPaymentOnly,
  getAddMainGuestCallState,
  (
    creditCardRequired,
    priceDifference,
    priceDifferenceAcknowledged,
    selectedPaymentMethodId,
    userSelectedTravelerId,
    bookingInProgress,
    email,
    phone,
    isTravelWalletPaymentOnly,
    addMainGuestCallState
  ) => {
    const paymentUnsettled =
      creditCardRequired &&
      !selectedPaymentMethodId &&
      !isTravelWalletPaymentOnly;

    if (
      (priceDifference.hasDifference && !priceDifferenceAcknowledged) ||
      paymentUnsettled ||
      !userSelectedTravelerId ||
      bookingInProgress ||
      !email ||
      !phone ||
      addMainGuestCallState !== CallState.Success
    ) {
      return false;
    }
    return true;
  }
);

export const getVacationRentalsBookingProgress = createSelector(
  getConfirmationEmail,
  getConfirmationPhoneNumber,
  getUserSelectedTravelerId,
  getSelectedPaymentMethodId,
  getRewardsPaymentAccountReferenceId,
  getVacationRentalsIsCreditCardPaymentRequired,
  (
    email,
    phoneNumber,
    traveler,
    selectedPayment,
    selectedRewardsAccount,
    creditCardRequired
  ) => {
    const paymentCompleted = () => {
      const unsettledPayment = !selectedPayment && creditCardRequired;

      if (selectedRewardsAccount && unsettledPayment) {
        return Progress.IN_PROGRESS;
      }
      if (!selectedRewardsAccount && !selectedPayment) {
        return Progress.NOT_STARTED;
      }
      return Progress.COMPLETED;
    };
    return [
      {
        name: textConstants.ADD_TRAVELERS,
        status: traveler ? Progress.COMPLETED : Progress.IN_PROGRESS,
      },
      {
        name: textConstants.CONTACT,
        status:
          email &&
          phoneNumber &&
          emailRegex.test(email) &&
          phoneRegex.test(phoneNumber)
            ? Progress.COMPLETED
            : Progress.NOT_STARTED,
      },
      {
        name: textConstants.REWARDS_AND_PAYMENT,
        status: paymentCompleted(),
      },
    ];
  }
);

export const areAllVacationRentalsStepsCompletedInCheckout = createSelector(
  getVacationRentalsBookingProgress,
  getVacationRentalsIsTravelWalletPaymentOnly,
  (checkoutProgress, isTravelWalletPaymentOnly) =>
    checkoutProgress[CheckoutSteps.ADD_TRAVELERS].status ===
      Progress.COMPLETED &&
    checkoutProgress[CheckoutSteps.CONTACT].status === Progress.COMPLETED &&
    (!isTravelWalletPaymentOnly
      ? checkoutProgress[CheckoutSteps.REWARDS_AND_PAYMENT].status ===
        Progress.COMPLETED
      : true)
);

export const vacationRentalPriceQuoteParamsSelector = createSelector(
  getVacationRentalShopSelectedListing,
  getFromDate,
  getUntilDate,
  getAdultsCount,
  getChildrenCount,
  getPetsCount,
  (
    selectedHome,
    checkIn,
    checkOut,
    adultsCount,
    childrenCount,
    petsCount
  ): CreateHomeProductRequest | null => {
    if (!selectedHome || !selectedHome.availability.rate) return null;
    return {
      listingId: selectedHome?.listingId,
      listingProviderId: selectedHome.listing.providerData.listingProviderId,
      stayDates: {
        from: dayjs(checkIn).format("YYYY-MM-DD"),
        until: dayjs(checkOut).format("YYYY-MM-DD"),
      },
      guests: {
        adults: adultsCount,
        children: childrenCount,
        infants: 0,
        petsIncluded: petsCount ? petsCount > 0 : false,
      },
      seenPrice: selectedHome.availability.rate.price.rentTotal,
      listingCollection: selectedHome.listing.listingCollection,
    };
  }
);

export const getVacationRentalsPaymentRequestType = createSelector(
  getTotalVacationRentalsCreditCardPaymentRequiredInFiatPrice,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentAccountReferenceId,
  getSelectedPaymentMethodId,
  (
    creditCardPayment,
    rewardsPayment,
    rewardsFiatPayment,
    rewardsAccountId,
    selectedPaymentMethodId
  ) => {
    if (selectedPaymentMethodId && !rewardsPayment) {
      return PaymentSplitRequestEnum.PaymentCardRequest;
    }
    if (
      rewardsAccountId &&
      rewardsPayment &&
      rewardsFiatPayment &&
      creditCardPayment?.value === 0
    ) {
      return PaymentSplitRequestEnum.PaymentRewardsRequest;
    }
    if (rewardsPayment && rewardsAccountId && rewardsFiatPayment) {
      return PaymentSplitRequestEnum.PaymentCardRewardsRequest;
    }
    return null;
  }
);

export const getVacationRentalsOpaquePayments = createSelector(
  getPaymentMethodRewardsAccountId,
  getTotalVacationRentalsCreditCardPaymentRequiredInFiatPrice,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentAccountReferenceId,
  getSelectedPaymentMethodId,
  getVacationRentalsPaymentRequestType,
  getTravelWalletItemsToApply,
  getVacationRentalsTravelWalletOfferToApplyAmount,
  getVacationRentalsTravelWalletCreditToApplyAmount,
  (
    accountReferenceId,
    creditCardPayment,
    rewardsPayment,
    rewardsFiatPayment,
    rewardsAccountId,
    paymentId,
    paymentRequestType,
    travelWalletItemsToApply,
    offerToApplyAmount,
    creditToApplyAmount
  ): PaymentOpaqueValue[] | null => {
    const amount: PaymentOpaqueValue[] = [];
    if (
      travelWalletItemsToApply.offerToApply ||
      travelWalletItemsToApply.creditToApply
    ) {
      if (travelWalletItemsToApply.offerToApply) {
        const offer: PaymentOpaqueValue = {
          type: Payment.TravelWalletOffer,
          value: {
            offerId: travelWalletItemsToApply.offerToApply.id,
            description:
              travelWalletItemsToApply.offerToApply?.descriptions[0] || "",
            paymentAmount: {
              fiatValue: {
                amount: roundToTwoDecimals(offerToApplyAmount),
                currency: travelWalletItemsToApply.offerToApply.amount.currency,
              },
            },
            PaymentV2: PaymentV2Enum.TravelWalletOffer,
          },
        };
        amount.push(offer);
      }
      if (travelWalletItemsToApply.creditToApply && creditToApplyAmount > 0) {
        const credit: PaymentOpaqueValue = {
          type: Payment.TravelWalletCredit,
          value: {
            offerId: travelWalletItemsToApply.creditToApply.id,
            description: "TravelWalletCredit", // creditToApply doesn't have a description
            paymentAmount: {
              fiatValue: {
                amount: roundToTwoDecimals(creditToApplyAmount),
                currency:
                  travelWalletItemsToApply.creditToApply.amount.currency,
              },
            },
            PaymentV2: PaymentV2Enum.TravelWalletCredit,
          },
        };
        amount.push(credit);
      }
    }
    switch (paymentRequestType) {
      case PaymentSplitRequestEnum.PaymentCardRequest:
        const userCardPayment: PaymentOpaqueValue = {
          type: Payment.Card,
          value: {
            paymentId: paymentId || "",
            accountReferenceId,
            paymentAmount: {
              currency: creditCardPayment!.currencyCode,
              amount: roundToTwoDecimals(creditCardPayment!.value),
            },
            PaymentV2: PaymentV2Enum.UserCard,
          },
        };
        amount.push(userCardPayment);
        break;
      case PaymentSplitRequestEnum.PaymentCardRewardsRequest:
        const splitUserCardPayment: PaymentOpaqueValue = {
          type: Payment.Card,
          value: {
            paymentId: paymentId || "",
            accountReferenceId,
            paymentAmount: {
              currency: creditCardPayment!.currencyCode,
              amount: roundToTwoDecimals(creditCardPayment!.value),
            },
            PaymentV2: PaymentV2Enum.UserCard,
          },
        };

        const splitRewardsPayment: PaymentOpaqueValue = {
          type: Payment.Rewards,
          value: {
            paymentAmount: {
              rewardsAccountId: rewardsAccountId!,
              fiatValue: {
                amount: roundToTwoDecimals(rewardsFiatPayment!.value),
                currency: rewardsFiatPayment!.currencyCode,
              },
              rewardsPrice: {
                value: roundToTwoDecimals(rewardsPayment!.value),
                currency: rewardsPayment!.currency,
              },
            },
            PaymentV2: PaymentV2Enum.Rewards,
          },
        };

        amount.push(splitUserCardPayment, splitRewardsPayment);
        break;
      case PaymentSplitRequestEnum.PaymentRewardsRequest:
        const rewardsPaymentType: PaymentOpaqueValue = {
          type: Payment.Rewards,
          value: {
            paymentAmount: {
              rewardsAccountId: rewardsAccountId!,
              fiatValue: {
                amount: roundToTwoDecimals(rewardsFiatPayment!.value),
                currency: rewardsFiatPayment!.currencyCode,
              },
              rewardsPrice: {
                value: roundToTwoDecimals(rewardsPayment!.value),
                currency: rewardsPayment!.currency,
              },
            },
            PaymentV2: PaymentV2Enum.Rewards,
          },
        };
        amount.push(rewardsPaymentType);
        break;
      default:
        break;
    }

    if (amount) {
      return amount;
    }
    return null;
  }
);

export const bookingVRInProgressSelector = createSelector(
  getScheduleVRBookCallState,
  getVRConfirmationDetailsCallState,
  (scheduleBookCallState, confirmationCallState) =>
    scheduleBookCallState === CallState.InProcess ||
    confirmationCallState === CallState.InProcess
);

const getErrorMessage = (error: PurchaseError): ErrorTitles => {
  const { code = "" } = error as ErrorCode;
  let agentSubtitle = getAgentErrorSubtitle(error.Error);
  let agentTitle = getAgentErrorTitle(error.Error);
  let titles = textConstants.GENERIC_VR_ERROR_TITLES;

  switch (error.Error) {
    case PurchaseErrorEnum.PaymentError:
      const paymentError = error as PaymentError;
      if (paymentError.value.type === Payment.Rewards)
        titles = textConstants.REDEMPTION_FAILED_TITLES;
      // Check for CardDeclined with custom backend messages
      if (paymentError.value.type === Payment.Card) {
        const cardDeclined = paymentError.value.value;
        if (cardDeclined.messageTitle && cardDeclined.messageBody) {
          titles = {
            icon: textConstants.ERROR_ICON,
            title: cardDeclined.messageTitle,
            subtitle: cardDeclined.messageBody,
            primaryButtonText: textConstants.EDIT_PAYMENT_INFO,
          };
        }
      }
      break;
    case PaymentErrorEnum.ErrorCode:
      agentSubtitle = getAgentErrorSubtitle(code);
      agentTitle = getAgentErrorTitle(code);

      switch (code) {
        case PaymentErrorEnum.UserCardNotFound:
          titles = textConstants.USER_CARD_ERROR_TITLES;
          break;
        case PaymentErrorEnum.FraudAutoReject:
        case PaymentErrorEnum.LikelyFraud:
          titles = textConstants.FRAUD_TITLES;
          break;
        case PaymentErrorEnum.RedemptionFailure:
          titles = textConstants.REDEMPTION_FAILED_TITLES;
          break;
      }
      break;
    case PurchaseErrorEnum.ProductError:
      const { errorEnum } = (error as ProductError).value.value;
      agentSubtitle = getAgentErrorSubtitle(errorEnum);
      agentTitle = getAgentErrorTitle(errorEnum);
      switch (errorEnum) {
        case PaymentErrorEnum.UserCardNotFound:
          titles = textConstants.USER_CARD_ERROR_TITLES;
          break;
        case PaymentErrorEnum.FraudAutoReject:
        case PaymentErrorEnum.LikelyFraud:
          titles = textConstants.FRAUD_TITLES;
          break;

        case PaymentErrorEnum.RedemptionFailure:
          titles = textConstants.REDEMPTION_FAILED_TITLES;
          break;
      }
      break;
    default:
  }

  return { ...titles, agentSubtitle, agentTitle };
};

export const getVRPriceQuoteErrors = (state: IStoreState) =>
  state.premierCollectionBook.vacationRentalsPriceQuoteErrors;

export const getVRSchedulePriceQuoteError = (state: IStoreState) =>
  state.premierCollectionBook.scheduleVacationRentalsPriceQuoteError;

export const getVRScheduleBookError = (state: IStoreState) =>
  state.premierCollectionBook.scheduleVacationRentalsBookError;

export const getVRHasError = createSelector(
  getUserPassengerCallState,
  getVRPriceDifference,
  getVRPriceDifferenceAcknowledged,
  getVacationRentalsPollPriceQuoteCallState,
  getVRScheduleBookError,
  getVRSchedulePriceQuoteError,
  getVRConfirmationDetailsCallState,
  getRewardsConversionFailed,
  getVerifyPaymentMethodCallState,
  (
    passengerCallState,
    priceDifference,
    priceDifferenceAcknowledged,
    priceQuoteCallState,
    scheduleBookError,
    schedulePriceQuoteError,
    confirmationDetailsCallState,
    rewardsConversionFailed,
    verifyPaymentMethodCallState
  ) =>
    passengerCallState === CallState.Failed ||
    (priceDifference.hasDifference && !priceDifferenceAcknowledged) ||
    !!rewardsConversionFailed ||
    !!scheduleBookError ||
    !!schedulePriceQuoteError ||
    verifyPaymentMethodCallState === CallState.Failed ||
    confirmationDetailsCallState === CallState.Failed ||
    priceQuoteCallState === CallState.Failed
);

export const getVRErrorTitles = createSelector(
  getVRHasError,
  getVRPriceQuoteErrors,
  getVRScheduleBookError,
  getVRSchedulePriceQuoteError,
  getVRConfirmationDetailsError,
  getVRPriceDifference,
  verifyPaymentMethodResultSelector,
  getRewardsAccounts,
  (
    hasError,
    priceQuoteErrors,
    scheduleBookError,
    schedulePriceQuoteError,
    confirmationDetailsErrors,
    priceDifference,
    verifyPaymentMethodResult,
    accounts
  ): ErrorTitles => {
    if (!hasError) return { title: "", subtitle: "", primaryButtonText: "" };

    if (priceDifference.hasDifference && priceDifference.amount) {
      const primaryButtonText = textConstants.CONTINUE;

      return {
        title: textConstants.getPriceDifferenceTitle(
          priceDifference.isIncrease,
          priceDifference.amount
        ),
        subtitle: priceDifference.isIncrease
          ? textConstants.VR_PRICE_INCREASE_SUBTITLE
          : textConstants.VR_PRICE_DECREASE_SUBTITLE,
        primaryButtonText,
        icon: priceDifference.isIncrease
          ? textConstants.PRICE_INCREASE_ICON
          : textConstants.PRICE_DECREASE_ICON,
      };
    }

    if (
      verifyPaymentMethodResult &&
      verifyPaymentMethodResult !== PaymentVerifyResultEnum.Success
    ) {
      const returnProps = paymentTextConstants.PAYMENT_METHOD_ERROR_TITLES(
        verifyPaymentMethodResult,
        accounts[0],
        isCorpTenant(config.TENANT)
      );

      return returnProps;
    }

    if (confirmationDetailsErrors.length > 0) {
      const returnProps = getErrorMessage(confirmationDetailsErrors[0]);

      return returnProps;
    }

    if (scheduleBookError) {
      const returnProps = textConstants.GENERIC_VR_ERROR_TITLES;

      return returnProps;
    }

    if (schedulePriceQuoteError) {
      const returnProps = textConstants.GENERIC_VR_ERROR_TITLES;

      return returnProps;
    }

    if (priceQuoteErrors.length > 0) {
      const returnProps = getErrorMessage(priceQuoteErrors[0]);

      return returnProps;
    }

    const returnProps = textConstants.GENERIC_VR_ERROR_TITLES;

    return returnProps;
  }
);

export const getCompleteBuyTotalPaymentProperties = createSelector(
  getTotalVacationRentalsCreditCardPaymentRequiredInFiatPrice,
  getRewardsPaymentInFiatCurrency,
  (
    totalCreditCardPaymentInFiat,
    totalRewardsInFiat,
  ) => ({
    properties: {
      total_rewards_amount_usd: totalRewardsInFiat?.value || 0,
      total_card_amount_usd: totalCreditCardPaymentInFiat?.value || 0,
    },
    encryptedProperties: [],
  })
);

export const getCompleteBuyVacationRentalProperties = createSelector(
  getViewedVacationRentalDetailsProperties,
  getVacationRentalConfirmationDetails,
  getPaymentMethods,
  getTabView,
  getRewardsPaymentAccount,
  getSelectedPaymentCardType,
  getOffers,
  getTravelWalletOfferToApplyAmount,
  getTravelWalletItemsToApply,
  getVacationRentalsTravelWalletCreditToApplyAmount,
  getIsSelfBooked,
  (
    trackingProperties,
    confirmationDetails,
    paymentMethods,
    tabView,
    account,
    cardType,
    offers,
    offerToApplyAmount,
    travelWalletItemsToApply,
    creditToApplyAmount,
    isSelfBooked,
  ): ITrackingProperties<CompleteBuyVacationRentalProperties> => ({
    properties: {
      ...trackingProperties.properties,
      success: false,
      reservation_id: confirmationDetails?.id.value ?? "",
      card_on_file: paymentMethods.length > 0,
      tab_view_on_availability: tabView ?? undefined,
      funnel_of_origin: tabView ? "stays" : "premium",
      card_product_used: cardType || "",
      rewards_product_used: account?.productDisplayName || "",
      ...travelWalletItemsToApply.offerToApply?.trackingPropertiesV2
        ?.properties,
      ...travelWalletItemsToApply.creditToApply?.trackingPropertiesV2
        ?.properties,
      offer_amount_redeemed_usd:
        !!travelWalletItemsToApply.offerToApply && !!offerToApplyAmount
          ? Math.abs(offerToApplyAmount)
          : 0,
      offer_count: offers?.length,
      offer_used: !!travelWalletItemsToApply.offerToApply,
      has_offer: !!offers && offers.length > 0,
      credit_redeemed:
        !!travelWalletItemsToApply.creditToApply?.amount.amount,
      credit_amt_redeemed: travelWalletItemsToApply.creditToApply
        ? Math.abs(creditToApplyAmount)
        : 0,
      booked_self_name_match: isSelfBooked,
    },
    encryptedProperties: [
      ...trackingProperties.encryptedProperties,
      travelWalletItemsToApply.offerToApply?.trackingPropertiesV2
        ?.encryptedProperties ?? "",
      travelWalletItemsToApply.creditToApply?.trackingPropertiesV2
        ?.encryptedProperties ?? "",
    ],
  })
);

export const getVRModalType = createSelector(
  getVRPriceQuoteErrors,
  getVRScheduleBookError,
  getVRSchedulePriceQuoteError,
  getVRConfirmationDetailsError,
  getVRPriceDifference,
  verifyPaymentMethodResultSelector,
  (
    priceQuoteErrors,
    scheduleBookError,
    schedulePriceQuoteError,
    confirmationDetailsErrors,
    priceDifference,
    verifyPaymentMethodResult
  ): string => {
    if (priceDifference.hasDifference && priceDifference.amount) {
      return "pc_price_difference";
    }

    if (
      verifyPaymentMethodResult &&
      verifyPaymentMethodResult !== PaymentVerifyResultEnum.Success
    ) {
      return "payment_verification_failed";
    }

    if (confirmationDetailsErrors.length > 0) {
      return "confirmation_details_failed";
    }

    if (scheduleBookError) {
      return "schedule_book_failed";
    }

    if (schedulePriceQuoteError) {
      return "schedule_price_quote_failed";
    }

    if (priceQuoteErrors.length > 0) {
      return "poll_price_quote_failed";
    }

    return "";
  }
);

export const getHomesOffersRequest = createSelector(
  getVacationRentalShopSelectedListing,
  getFromDate,
  getUntilDate,
  getVacationRentalsPriceQuotePricing,
  (
    selectedHome,
    fromDate,
    untilDate,
    priceQuotePricing
  ): OffersForProductsRequest | null => {
    if (selectedHome && fromDate && untilDate) {
      return {
        products: [
          {
            AvailableProduct: AvailableProductEnum.Home,
            listingId: selectedHome.listing.id.id,
            merchant: MerchantOfRecord.Hopper,
            market: selectedHome.listing.market?.id,
            stayDates: {
              from: dayjs(fromDate).format("YYYY-MM-DD"),
              until: dayjs(untilDate).format("YYYY-MM-DD"),
            },
            belongsToPackage: false,
            productPricing: {
              productClass: ProductClassEnum.Economy,
              currency:
                priceQuotePricing?.detailedPrice.payNowTotal.fiat
                  .currencyCode ??
                selectedHome.availability.rate?.price.total.fiat.currencyCode ??
                "USD",
              basePrice:
                priceQuotePricing?.detailedPrice.sellPrice.fiat.value ??
                selectedHome.availability.rate?.price.rentTotal.fiat.value ??
                0,
              grossBookValue:
                priceQuotePricing?.detailedPrice.payNowTotal.fiat.value ??
                selectedHome.availability.rate?.price.total.fiat.value ??
                0,
              ancillaryTotal: 0, // hardcoded bc Tysons does this https://github.com/hopper-org/Tysons/blob/1167df0bdaa8ae2a5255ea0203438deacff071ae/server/src/main/scala/com/hopper/tysons/service/hotels/availability/converter/ImpFastAvailabilityResponseConverter.scala#L552
              // cap1 does not have discounts so can use total price
              preDiscountTotal:
                priceQuotePricing?.detailedPrice.payNowTotal.fiat.value ??
                selectedHome.availability.rate?.price.total.fiat.value ??
                0,
              preDiscountGrandTotal:
                priceQuotePricing?.detailedPrice.payNowTotal.fiat.value ??
                selectedHome.availability.rate?.price.total.fiat.value ??
                0,
              sellPrice:
                priceQuotePricing?.detailedPrice.sellPrice.fiat.value ??
                selectedHome.availability.rate?.price.total.fiat.value ??
                0,
            },
            paymentSchedule: PaymentSchedule.PayNow,
            isHostFunded: false,
            hostId: selectedHome?.listing.privateHostInfo?.id,
            collectionType: selectedHome.listing.listingCollection,
          } as AvailableProduct,
        ],
      };
    }
    return null;
  }
);
